import { ColumnDef } from "@tanstack/react-table";
import { getLinkToWithNewSearchParameter } from "helpers";
import { t } from "i18next";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Checkbox,
  ItemsTable,
  TableItemStatus,
  TableItemStatusControl
} from "ui";

import { PersonalOrderBadge } from "../../../orders/components";
import { ReturnStatus } from "../../constants";
import { Return } from "../../returns.types";
import { OrderCell, OrderPersonCell, ReturnModals } from "./atoms";
import { useReturnModals, useReturnStatuses } from "./hooks";
import { ReturnsTableProps } from "./ReturnsTable.types";

import styles from "./ReturnsTable.module.scss";

const getPrice = (price?: number) => {
  if (price) return Math.floor(price).toLocaleString("en-US");
  else return 0;
};

const ReturnsTable = ({
  returnsList,
  sorting,
  setSorting,
  isLoading,
  refetchReturns,
  selectedReturnsIds,
  setSelectedReturnsIds
}: ReturnsTableProps) => {
  const [searchParams] = useSearchParams();
  const {
    approveModal,
    rejectModal,
    openedItemStatusesIndex,
    setOpenedItemStatusesIndex,
    actionConfirmingIndex,
    setActionConfirmingIndex
  } = useReturnModals();

  const { statusesList, changingStatusesList, getOrderStatusIndex } =
    useReturnStatuses({
      openedItemStatusesIndex,
      setActionConfirmingIndex,
      openApproveModal: approveModal.setTrue,
      openRejectModal: rejectModal.setTrue
    });

  const columns: Array<ColumnDef<Return, Return[]>> = useMemo(
    () => [
      {
        id: "select",
        header: () => {
          const isChecked = returnsList.some((returnItem) =>
            selectedReturnsIds?.includes(returnItem.id)
          );
          const isFullyChecked = returnsList.every((returnItem) =>
            selectedReturnsIds?.includes(returnItem.id)
          );

          return (
            <Checkbox
              checked={isChecked}
              partiallyChecked={!isFullyChecked}
              onChange={() => {
                if (isChecked) {
                  setSelectedReturnsIds?.([]);
                } else {
                  setSelectedReturnsIds?.((prev) => [
                    ...prev,
                    ...returnsList.map((returnItem) => returnItem.id)
                  ]);
                }
              }}
            />
          );
        },
        size: 3,
        minSize: 3,
        cell: ({ row }) => {
          const isSelected = selectedReturnsIds?.includes(
            returnsList[row.index].id
          );

          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isSelected}
                onChange={() => {
                  setSelectedReturnsIds?.((prev) =>
                    isSelected
                      ? prev.filter((id) => id !== returnsList[row.index].id)
                      : [...prev, returnsList[row.index].id]
                  );
                }}
              />
            </div>
          );
        }
      },
      {
        header: t("Order"),
        size: 14,
        minSize: 14,
        cell: ({ row }) => <OrderCell returnItem={returnsList[row.index]} />
      },
      {
        header: t("User"),
        size: 14,
        minSize: 14,
        cell: ({ row }) => (
          <OrderPersonCell
            imageUrl={returnsList[row.index].resellerImageUrl}
            name={returnsList[row.index].resellerName}
          />
        )
      },
      {
        id: "orderTotal",
        accessorKey: "orderTotal",
        header: t("Order total"),
        size: 12,
        minSize: 12,
        cell: ({ row }) => getPrice(returnsList[row.index].orderTotal)
      },
      {
        id: "totalCommission",
        accessorKey: "totalCommission",
        header: t("Commission"),
        size: 11.5,
        minSize: 11.5,
        cell: ({ row }) => getPrice(returnsList[row.index].totalCommission)
      },
      {
        id: "totalMargin",
        accessorKey: "totalMargin",
        header: t("Margin"),
        size: 8.5,
        minSize: 8.5,
        cell: ({ row }) => getPrice(returnsList[row.index].totalMargin)
      },
      {
        header: t("Driver"),
        size: 13,
        minSize: 13,
        cell: ({ row }) => (
          <OrderPersonCell
            imageUrl={returnsList[row.index].driverImageUrl}
            name={returnsList[row.index].driverName}
          />
        )
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t("Order date"),
        size: 9,
        minSize: 9,
        cell: ({ row }) => (
          <pre>
            {`${moment(returnsList[row.index].createdAt).format("DD/MM/YYYY")}`}
            {"\n"}
            {`${moment(returnsList[row.index].createdAt).format("HH:mm")}`}
          </pre>
        )
      },
      {
        id: "returnRequestedAt",
        accessorKey: "returnRequestedAt",
        header: t("Request date"),
        size: 9,
        minSize: 9,
        cell: ({ row }) => (
          <pre>
            {`${moment(returnsList[row.index].returnRequestedAt).format("DD/MM/YYYY")}`}
            {"\n"}
            {`${moment(returnsList[row.index].returnRequestedAt).format("HH:mm")}`}
          </pre>
        )
      },
      {
        header: t("Status"),
        size: 9,
        minSize: 9,
        cell: ({ row }) => (
          <div className={styles.returnsTable_statusCell}>
            {returnsList[row.index].status === ReturnStatus.ReturnPlaced ? (
              <TableItemStatusControl
                rowIndex={row.index}
                activeStatusIndex={getOrderStatusIndex(
                  returnsList[row.index].status,
                  changingStatusesList
                )}
                openedItemStatusesIndex={openedItemStatusesIndex}
                setOpenedItemStatusesIndex={setOpenedItemStatusesIndex}
                statusesList={changingStatusesList}
                hideDisabled
              />
            ) : (
              <TableItemStatus
                activeStatusIndex={getOrderStatusIndex(
                  returnsList[row.index].status,
                  statusesList
                )}
                statusesList={statusesList}
              />
            )}
            {returnsList[row.index].isPersonalOrder && <PersonalOrderBadge />}
          </div>
        )
      }
    ],
    [returnsList, selectedReturnsIds, openedItemStatusesIndex]
  );

  return (
    <>
      <ItemsTable
        className={styles.returnsTable}
        columns={columns}
        data={returnsList}
        sorting={sorting}
        setSorting={setSorting}
        isLoading={isLoading}
        skeletonHeight={65}
        getLinkUrl={({ id }) => {
          return getLinkToWithNewSearchParameter({
            searchParams,
            parameterName: "returnId",
            parameterValue: id
          });
        }}
      />

      <ReturnModals
        approveModal={approveModal}
        rejectModal={rejectModal}
        returnId={returnsList[actionConfirmingIndex as number]?.id}
        refetchReturns={refetchReturns}
        actionConfirmingIndex={actionConfirmingIndex}
        setActionConfirmingIndex={setActionConfirmingIndex}
      />
    </>
  );
};

export default ReturnsTable;
